import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Search as SearchIcon,
  Refresh,
  RequestQuote,
  Inbox,
  HourglassTop,
  EmojiEvents,
  FilterList,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { procurementService, RfqRow, RfqStatus, RfqSummary, RfqType } from '../../services/procurementService';

// ---------------------------------------------------------------------------
// RFQ list -- the front door of the sourcing workbench. Mirrors
// PurchaseRequisitionPage.tsx's shape: summary stat cards, a filter bar, a
// single results table, row click -> detail.
// ---------------------------------------------------------------------------

const RFQ_TYPES: RfqType[] = ['MATERIAL', 'SERVICE', 'CAPEX', 'SUBCONTRACTING', 'JOB_WORK', 'CONSUMABLE', 'ASSET', 'OTHER'];

const STATUS_OPTIONS: RfqStatus[] = ['DRAFT', 'ISSUED', 'RESPONSES_IN', 'UNDER_EVALUATION', 'AWARDED', 'CANCELLED', 'CLOSED'];

const STATUS_TONE: Record<RfqStatus, StatusTone> = {
  DRAFT: 'neutral',
  ISSUED: 'info',
  RESPONSES_IN: 'warning',
  UNDER_EVALUATION: 'warning',
  AWARDED: 'success',
  CANCELLED: 'neutral',
  CLOSED: 'neutral',
};

const TYPE_TINT: Record<string, string> = {
  MATERIAL: '#0284c7',
  SERVICE: '#7c3aed',
  CAPEX: '#dc2626',
  SUBCONTRACTING: '#d97706',
  JOB_WORK: '#059669',
  CONSUMABLE: '#64748b',
  ASSET: '#0f766e',
  OTHER: '#475569',
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const RfqPage: React.FC = () => {
  const navigate = useNavigate();

  const [rfqs, setRfqs] = useState<RfqRow[]>([]);
  const [summary, setSummary] = useState<RfqSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<RfqStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<RfqType | ''>('');
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      procurementService.getRfqs({
        status: statusFilter || undefined,
        rfqType: typeFilter || undefined,
        search: search || undefined,
      }),
      procurementService.getRfqSummary(),
    ])
      .then(([rfqRes, summaryRes]) => {
        setRfqs(rfqRes.rfqs);
        setSummary(summaryRes.summary);
      })
      .catch((err: any) => setError(err.message || 'Failed to load RFQs'))
      .finally(() => setLoading(false));
  }, [statusFilter, typeFilter, search]);

  useEffect(() => {
    const handle = setTimeout(fetchAll, search ? 350 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, search]);

  const awardedCount = summary?.byStatus?.AWARDED?.count ?? (summary ? 0 : null);

  const hasFilters = !!(statusFilter || typeFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="RFQ / Sourcing"
        subtitle="Send requests for quotation, collect supplier bids and compare them side by side before awarding"
        actions={
          <>
            <IconButton onClick={fetchAll} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => navigate('/procurement/rfqs/new')}>
              New RFQ
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <StatCard title="Total RFQs" value={summary ? summary.totalCount : null} icon={<RequestQuote />} color="#2563eb" />
        <StatCard title="Open" value={summary ? summary.openCount : null} icon={<Inbox />} color="#0284c7" />
        <StatCard
          title="Awaiting Response"
          value={summary ? summary.awaitingResponseCount : null}
          icon={<HourglassTop />}
          color="#d97706"
        />
        <StatCard title="Awarded" value={awardedCount} icon={<EmojiEvents />} color="#059669" />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="RFQ # or notes"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as RfqStatus | '')}
          sx={{ minWidth: 190 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as RfqType | '')}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {RFQ_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['RFQ', 'Type', 'Status', 'Suppliers', 'Quotations', 'Deadline', 'Awarded To'].map((h, i) => (
                  <TableCell key={h} align={i === 3 || i === 4 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rfqs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState title="No RFQs match these filters" hint="Clear the filters, or create a new RFQ." />
                  </TableCell>
                </TableRow>
              ) : (
                rfqs.map((rfq) => (
                  <TableRow
                    key={rfq.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/procurement/rfqs/${rfq.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell
                        code={rfq.rfq_number}
                        tint={TYPE_TINT[rfq.rfq_type] || '#475569'}
                        icon={<RequestQuote fontSize="small" />}
                      />
                    </TableCell>
                    <TableCell>
                      <TypePill label={rfq.rfq_type.replace(/_/g, ' ')} tint={TYPE_TINT[rfq.rfq_type] || '#475569'} />
                    </TableCell>
                    <TableCell>
                      <StatusDot label={rfq.status.replace(/_/g, ' ')} tone={STATUS_TONE[rfq.status]} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {rfq.supplier_count ?? '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {rfq.quotation_count ?? '—'}
                    </TableCell>
                    <TableCell>{fmtDate(rfq.response_deadline)}</TableCell>
                    <TableCell>
                      {rfq.awarded_vendor_name ? (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {rfq.awarded_vendor_name}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default RfqPage;
